import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { rotaOculta } from '../config/navigationConfig';

const FORCE_PASSWORD_PATH = '/trocar-senha';

/**
 * Componente para proteger rotas autenticadas
 * Redireciona para login se não estiver autenticado.
 * Se o usuário precisa trocar a senha (primeiro acesso ou reset por
 * admin), redireciona para a tela bloqueante de troca de senha.
 */
export function PrivateRoute({ children }) {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white">Carregando...</div>
      </div>
    );
  }

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  if (user?.must_change_password && location.pathname !== FORCE_PASSWORD_PATH) {
    return <Navigate to={FORCE_PASSWORD_PATH} replace />;
  }

  // Aba escondida desta conta em Configurações > Acessos por Usuário: sai do
  // menu e o endereço também deixa de abrir.
  if (rotaOculta(user, location.pathname)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

/**
 * Componente para proteger rotas baseadas em roles específicas
 * Útil para páginas que só determinados roles podem acessar
 *
 * Props:
 * - allowedRoles: array de roles permitidas para acessar a rota
 * - deniedRoles: papéis barrados mesmo que um alias os libere (ex.: o
 *   Secretário passa por Coordenador em `allowedRoles`, mas não entra no RH)
 * - redirectTo: rota para redirecionar se não tiver permissão (default: /dashboard)
 */
export function RoleBasedRoute({ children, allowedRoles, deniedRoles = [], redirectTo = '/dashboard' }) {
  const { isAuthenticated, hasAnyRole, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white">Carregando...</div>
      </div>
    );
  }

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  if (user?.must_change_password) {
    return <Navigate to={FORCE_PASSWORD_PATH} replace />;
  }

  if (deniedRoles.includes(user?.role) || !hasAnyRole(allowedRoles)) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
}

/**
 * Componente que redireciona usuário autenticado para dashboard
 * Útil para a página de login - redireciona se já estiver logado
 */
export function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white">Carregando...</div>
      </div>
    );
  }

  if (isAuthenticated()) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

export default RoleBasedRoute;
